import type { SaleType } from '@thevillages/schemas';

import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import UniqueValueRenderer from '@arcgis/core/renderers/UniqueValueRenderer';
// import FeatureReductionCluster from '@arcgis/core/layers/support/FeatureReductionCluster';
import SimpleMarkerSymbol from '@arcgis/core/symbols/SimpleMarkerSymbol';

import { ListingColor } from '../utils/listingColor';
// import { ClusterLabels } from './cluster-layer/cluster-labels';
import { PriceLabels } from './cluster-layer/price-labels';

// Creates a dedicated, client-side (memory-only) point layer for a single sale type, with clustering enabled
// so the number of graphics rendered shrinks as the user zooms out.
export const SaleTypeLayer = (saleType: SaleType): FeatureLayer => {
  return new FeatureLayer({
    fields: [
      { name: 'ObjectID', type: 'oid' },
      { name: 'id', type: 'string' },
      { name: 'pending', type: 'small-integer' },
      { name: 'saved', type: 'small-integer' },
      { name: 'price', type: 'string' },
    ],
    geometryType: 'point',
    // Comment/Uncomment the following block to enable/disable clustering for this layer.
    // featureReduction: new FeatureReductionCluster({
    //   clusterRadius: '100px',
    //   clusterMinSize: '24px',
    //   clusterMaxSize: '60px',
    //   labelingInfo: [ClusterLabels],
    //   maxScale: 4000,
    //   popupEnabled: false,
    // }),
    labelingInfo: [PriceLabels],
    objectIdField: 'ObjectID',
    outFields: ['id'],
    popupEnabled: false,
    renderer: new UniqueValueRenderer({
      defaultSymbol: createMarkerSymbol(ListingColor(saleType)),
      uniqueValueInfos: [
        { symbol: createSavedSymbol(), value: 'saved' },
        { symbol: createMarkerSymbol(ListingColor(saleType, true)), value: 'pending' },
        { symbol: createMarkerSymbol(ListingColor(saleType)), value: 'default' },
      ],
      valueExpression:
        "When($feature.saved == 1, 'saved', $feature.pending == 1, 'pending', 'default')",
    }),
    source: [],
    spatialReference: { wkid: 4326 },
    title: saleType,
  });
};

function createMarkerSymbol(color: string): SimpleMarkerSymbol {
  return new SimpleMarkerSymbol({
    color,
    outline: { color: 'white', width: 2 },
    size: 12,
    style: 'circle',
  });
}

function createSavedSymbol(): SimpleMarkerSymbol {
  const _symbol = new SimpleMarkerSymbol({
    color: [252, 9, 6, 1],
    outline: { color: 'white', width: 2 },
    size: 16,
    style: 'circle',
  });

  _symbol.path =
    'm480-120-58-52q-101-91-167-157T150-447.5Q111-500 95.5-544T80-634q0-94 63-157t157-63q52 0 99 22t81 62q34-40 81-62t99-22q94 0 157 63t63 157q0 46-15.5 90T810-447.5Q771-395 705-329T538-172l-58 52Z';

  return _symbol;
}
